import React from 'react';
import { CheckCircle2, Download, Lock, ArrowRightLeft, AlertTriangle, ScrollText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Plus } from 'lucide-react';
import { invoices as invoicesApi } from '@/lib/api';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { trust, matters as mattersApi } from '@/lib/api';

export default function TrustAccountingPage() {
  const [summary, setSummary] = React.useState(null);
  const [txns, setTxns] = React.useState([]);
  const [mList, setMList] = React.useState([]);
  const [recon, setRecon] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(() => {
    setLoading(true);
    Promise.all([trust.summary(), trust.transactions(), mattersApi.list(), trust.reconciliation()])
      .then(([s, t, m, r]) => { setSummary(s); setTxns(t); setMList(m); setRecon(r); })
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(load, [load]);

  if (loading) return <div className="flex h-full items-center justify-center p-16"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">Trust &amp; Operating Accounting</h1>
          <div className="text-sm text-slate-500">OMARA cl. 7 aligned · zero-negative ledger enforced by API</div>
        </div>
        <div className="flex gap-2">
          <TransferDialog matters={mList} onDone={load} />
          <AddInvoiceDialog matters={mList} onDone={load} />
          <Button variant="outline" className="h-10 gap-1.5 border-slate-200"><Download className="h-4 w-4" /> Audit PDF</Button>
          <Button onClick={load} className="h-10 gap-1.5 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">Refresh</Button>
        </div>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-3">
        <Card className="border-slate-200"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500">Trust account</div>
          <div className="mt-2 font-serif text-3xl font-bold text-[#1A3A5C]">A${summary.trust_total.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</div>
          <div className="mt-1 flex items-center gap-1 text-xs text-emerald-600"><CheckCircle2 className="h-3 w-3" /> Reconciled {summary.reconciled_at}</div>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500">Operating account</div>
          <div className="mt-2 font-serif text-3xl font-bold text-[#1A3A5C]">A${summary.operating_total.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</div>
          <div className="mt-1 text-xs text-slate-500">Movement tracked live</div>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500">Client ledgers (open matters)</div>
          <div className="mt-2 font-serif text-3xl font-bold text-[#1A3A5C]">{summary.open_client_ledgers}</div>
          <div className="mt-1 text-xs text-slate-500">Sum equals trust cashbook ✓</div>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="ledger" className="mt-8">
        <TabsList>
          <TabsTrigger value="ledger">Per-matter ledger</TabsTrigger>
          <TabsTrigger value="transactions">Recent transactions</TabsTrigger>
          <TabsTrigger value="recon">Three-way reconciliation</TabsTrigger>
        </TabsList>

        <TabsContent value="ledger" className="mt-6">
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-4 py-3 text-left">Matter</th><th className="px-4 py-3 text-left">Applicant</th><th className="px-4 py-3 text-left">Subclass</th><th className="px-4 py-3 text-right">Balance (A$)</th><th className="px-4 py-3">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {mList.filter((m) => (m.trust_balance || 0) > 0).map((m) => (
                  <tr key={m.id} className="transition-colors hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{m.ref}</td>
                    <td className="px-4 py-3 font-medium text-[#1A3A5C]">{m.applicant}</td>
                    <td className="px-4 py-3">{m.subclass}</td>
                    <td className="px-4 py-3 text-right font-mono font-medium text-[#1A3A5C]">{m.trust_balance.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3"><Badge variant="secondary" className="bg-emerald-100 text-emerald-700">Reconciled</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="transactions" className="mt-6">
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">Ref</th><th className="px-4 py-3 text-left">Description</th><th className="px-4 py-3 text-left">Account</th><th className="px-4 py-3 text-right">Debit</th><th className="px-4 py-3 text-right">Credit</th><th className="px-4 py-3 text-right">Balance</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {txns.map((t) => (
                  <tr key={t.id} className="transition-colors hover:bg-slate-50">
                    <td className="px-4 py-3 text-xs">{new Date(t.at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{t.reference_number}</td>
                    <td className="px-4 py-3">{t.description}</td>
                    <td className="px-4 py-3"><Badge variant="secondary" className={t.account_type === 'TRUST' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}>{t.account_type}</Badge></td>
                    <td className="px-4 py-3 text-right font-mono">{t.debit_amount ? t.debit_amount.toFixed(2) : '—'}</td>
                    <td className="px-4 py-3 text-right font-mono">{t.credit_amount ? t.credit_amount.toFixed(2) : '—'}</td>
                    <td className="px-4 py-3 text-right font-mono font-medium">{t.balance_after.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="recon" className="mt-6">
          <div className="rounded-lg border border-slate-200 bg-white p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-[#1A3A5C]">Three-way reconciliation — live</div>
                <div className="text-xs text-slate-500">Period {recon.period}</div>
              </div>
              <Badge className={recon.variance === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{recon.status}</Badge>
            </div>
            <div className="mt-5 grid gap-6 md:grid-cols-2">
              <div className="space-y-2 text-sm">
                <ReconRow label="Trust cashbook running balance" value={recon.cashbook_balance} strong />
                <div className="border-t border-slate-200 pt-2"><ReconRow label="Adjusted internal balance" value={recon.adjusted_internal} strong /></div>
                <ReconRow label="Bank statement balance" value={recon.bank_statement} strong />
                <div className="rounded-md bg-emerald-50 p-3"><ReconRow label="Variance" value={recon.variance} tone="emerald" strong /></div>
                <div className="border-t border-slate-200 pt-2"><ReconRow label="Sum of client ledgers" value={recon.ledger_sum} strong /></div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#2ECC9A]"><Lock className="h-3.5 w-3.5" /> System invariants</div>
                {['Zero-negative ledger enforced by API', 'Debit XOR credit — no dual-sided entries', 'Trust cashbook = sum of client ledgers', 'Trust→Operating requires closing statement + consent'].map((r) => (
                  <div key={r} className="flex items-start gap-2 text-slate-700"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {r}</div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ReconRow({ label, value, strong, tone }) {
  return (
    <div className="flex justify-between text-slate-700">
      <span>{label}</span>
      <span className={`font-mono ${strong ? 'font-semibold' : ''} ${tone === 'emerald' ? 'text-emerald-700' : ''}`}>
        A${Math.abs(value).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
      </span>
    </div>
  );
}

function TransferDialog({ matters, onDone }) {
  const [open, setOpen] = React.useState(false);
  const eligible = matters.filter((m) => (m.trust_balance || 0) > 0);
  const [matterId, setMatterId] = React.useState(eligible[0]?.id || '');
  React.useEffect(() => { setMatterId(eligible[0]?.id || ''); }, [matters.length]);  // eslint-disable-line
  const selected = eligible.find((m) => m.id === matterId);

  const [amount, setAmount] = React.useState('2500');
  const [ref, setRef] = React.useState('INV-' + Math.floor(Math.random()*9000+1000));
  const [statement, setStatement] = React.useState('Dear client,\n\nWe write to confirm the successful lodgement of your application. Please find enclosed our final invoice and a statement of services. Going forward, we will notify you of any DHA correspondence within 1 business day. Kind regards.');
  const [consent, setConsent] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [ok, setOk] = React.useState(null);

  const submit = async (e) => {
    e.preventDefault(); setErr(''); setSaving(true);
    try {
      const r = await trust.transfer({ matter_id: matterId, amount: Number(amount), reference_number: ref, closing_statement: statement, client_consent: consent });
      setOk(r); onDone?.();
    } catch (er) { setErr(er?.response?.data?.detail || 'Transfer failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v)=>{ setOpen(v); if (!v){ setOk(null); setErr(''); setConsent(false); } }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="h-10 gap-1.5 border-slate-200"><ArrowRightLeft className="h-4 w-4" /> Trust → Operating</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Pre-transfer consent gate</DialogTitle></DialogHeader>
        {!ok && (
          <form onSubmit={submit} className="space-y-4 text-sm">
            <div className="rounded-md bg-amber-50 p-3 text-xs text-amber-800">
              <div className="flex items-start gap-2"><AlertTriangle className="mt-0.5 h-3.5 w-3.5" /><span>OMARA cl. 7: client consent + closing statement required before releasing fees from trust.</span></div>
            </div>
            <div><Label>Matter</Label>
              <Select value={matterId} onValueChange={setMatterId}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {eligible.map((m) => <SelectItem key={m.id} value={m.id}>{m.ref} — {m.applicant} (A${m.trust_balance.toLocaleString()})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount (A$)</Label><Input type="number" step="0.01" value={amount} onChange={(e)=>setAmount(e.target.value)} className="mt-1" /></div>
              <div><Label>Reference</Label><Input value={ref} onChange={(e)=>setRef(e.target.value)} className="mt-1" /></div>
            </div>
            {selected && <div className="text-xs text-slate-500">Available: A${selected.trust_balance.toLocaleString()}</div>}
            <div><Label>Closing statement to client</Label><Textarea rows={5} value={statement} onChange={(e)=>setStatement(e.target.value)} className="mt-1" /></div>
            <div className="flex items-start gap-2">
              <Checkbox id="cg" checked={consent} onCheckedChange={(v)=>setConsent(!!v)} />
              <label htmlFor="cg" className="text-xs text-slate-600 cursor-pointer">Client&rsquo;s e-signed consent to release captured in portal.</label>
            </div>
            {err && <div className="rounded-md bg-red-50 p-2 text-xs text-red-700">{err}</div>}
            <DialogFooter>
              <Button type="submit" disabled={saving || !consent} className="bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
                {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ScrollText className="mr-1 h-4 w-4" />} Send statement &amp; release
              </Button>
            </DialogFooter>
          </form>
        )}
        {ok && (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2 text-emerald-600"><CheckCircle2 className="h-5 w-5" /><span className="font-semibold">Transfer authorised.</span></div>
            <div className="text-slate-700">A${Number(amount).toLocaleString()} released. New trust balance: <span className="font-mono font-semibold">A${ok.new_trust_balance.toLocaleString()}</span>. Full audit trail written.</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AddInvoiceDialog({ matters, onDone }) {
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState({ matter_ref: '', description: '', amount: '' });
  const [busy, setBusy] = React.useState(false);
  const submit = async () => {
    if (!form.matter_ref || !form.description.trim() || !form.amount) { toast.error('All fields required'); return; }
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt <= 0) { toast.error('Amount must be positive'); return; }
    setBusy(true);
    try {
      await invoicesApi.create({ matter_ref: form.matter_ref, description: form.description.trim(), amount: amt });
      toast.success('Invoice created');
      setOpen(false); setForm({ matter_ref: '', description: '', amount: '' });
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="h-10 gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700" data-testid="trust-add-invoice">
          <Plus className="h-4 w-4" /> Add invoice
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="trust-invoice-dialog">
        <DialogHeader><DialogTitle>Add invoice</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Matter</label>
            <Select value={form.matter_ref} onValueChange={(v) => setForm({ ...form, matter_ref: v })}>
              <SelectTrigger data-testid="trust-invoice-matter"><SelectValue placeholder="Select matter…" /></SelectTrigger>
              <SelectContent>
                {matters.map((m) => <SelectItem key={m.ref} value={m.ref}>{m.ref} · {typeof m.applicant === 'string' ? m.applicant : m.applicant?.name || m.applicant_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Description</label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Professional fees — 189 lodgement" data-testid="trust-invoice-desc" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Amount (AUD)</label>
            <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="1500" data-testid="trust-invoice-amount" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="trust-invoice-submit">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

